import Fuse from 'fuse.js';

import { getUnit } from '../../../../../store/utils';

const staticAbbr = [
    'STAT',
    'SOS',
    'alternate days',
    'bi every week',
    'twice daily',
    'once daily',
    'daily',
    'weekly',
    'regular',
    'continous',
    'bid',
    'stat',
    'tid',
    'bd',
    'od',
    'qid',
    'tds',
    'eod',
    'everyday',
    'hs',
    'as needed',
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'monday, wednesday, friday',
    'tuesday, thursday, saturday',
    'second day',
    'full day',
    'sixth day',
    'fourth day',
    'fifth day',
    'third day',
    'first day',
];

// TODO: ignore distance
const fuseConfig = {
    threshold: 0.1,
};

const staticFuse = new Fuse(staticAbbr, fuseConfig);

// const codedHypenPattern = /\d+((-)\d+((-)\d+((-)\d+)?)?)?/gm;
const codedHypenPattern = /\d+(\/(\d+)?)?((-)\d+(\/(\d+)?)?((-)\d+(\/(\d+)?)?((-)\d+(\/(\d+)?)?)?)?)?/gim;
const balckListedPatterns = new Set(['0-0-0', '0-0-0-0']);
const supportedDosesFractions = ['1/2', '1/4', '3/4', '1/3', '2/3'];
const supportedDosesFractionsSet = new Set(supportedDosesFractions);
const supportedDoses = ['0', '1', ...supportedDosesFractions, '2'];
const supportedDosesSet = new Set(supportedDoses);
const maxDoseInTheCodePatten = 4;
const minDoseInTheCodePatten = 3;

const multiplicativeAdversList = ['once', 'twice', 'thrice'];
const multiplicativeAdverbs = new Set(multiplicativeAdversList);
const temporalNouns = ['a day', 'a week', 'a month', 'an hour'];
const pureTemporalNouns = ['hour', 'day', 'week', 'month', 'year'];

// const temporalNounRegexWithNumber = /^\d+(\s)?(time(s)?)?(\s)?(an hour|a day|a month|a week|a year)?$/gm;
const temporalNounRegexWithNumber = /^(\d+)?(\s)?(time(s)?)?(\s)?((a)?(\s)?w(e(ek)?)?|(a)?(\s)?m(o(nth)?)?|(a)?(\s)?d(a(y)?)?|(an)?(\s)?h(o(ur)?)?)?/gim;

const temporalNounRegex = /^(o(n)?(c)?(e)?|((t)(w|h)?(i|r)?(c|i)?(e|c)?(e)?))?(\s)?((a)?(\s)?w(e(ek)?)?|(a)?(\s)?m(o(nth)?)?|(a)?(\s)?d(a(y)?)?|(an)?(\s)?h(o(ur)?)?)?/gim;

const everyXHourly = /^(ev(ery)?)?(\s)?([\d]+)?(\s)?(ho(urly)?)?/gim;

// const onceEvery = /^(on(c(e)?)?)?(\s)?(ev(ery)?)?(\s)?(\d+)?(d(ay)?|days|w(eek)?|weeks|mo(nth)?|months|y(ear)?|years)?/gm;
const onceEvery = /^(o(n)?(c)?(e)?|((t)(w|h)?(i|r)?(c|i)?(e|c)?(e)?))?(\s)?(ev(ery)?)?(\s)?(\d+)?(\s)?(d(ay)?|days|w(eek)?|weeks|mo(nth)?|months|ho(ur)?|ho(ous)|y(ear)?|years)?/gim;

export const onFrequencySearch = async (
    text: string,
    genie_MED_FREQUENCY: { n: string }[],
): Promise<string[]> => {
    if (!text.trim()) {
        return new Promise((resolve, _) => {
            const loweredText = text.toLowerCase();
            const options = genie_MED_FREQUENCY
                .filter((t) => t.n.toLowerCase().includes(loweredText))
                .map((t) => t.n);
            if (text && !options.find((t) => t.toLowerCase() === loweredText)) {
                options.push(text);
            }
            resolve(options);
        });
    }

    const allOptions: string[] = [];

    const parts = text
        .split(' ')
        .map((t) => t.trim())
        .filter(Boolean);

    const nos = parts
        .map((t) => parseInt(t))
        .filter((t) => !isNaN(t) && t > 0)
        .map((t) => t.toString());

    if (supportedDosesSet.has(parseInt(text).toString())) {
        allOptions.push(...appendCodedOptions(text));
    }

    const isTemporalNounRegexPattern = text.match(temporalNounRegex);
    if (
        isTemporalNounRegexPattern &&
        isTemporalNounRegexPattern.length &&
        isTemporalNounRegexPattern[0].length &&
        text.indexOf('-') === -1
    ) {
        if (parts[0].trim()) {
            const temporalNounFound =
                multiplicativeAdverbs.has(parts[0].trim().toLowerCase()) ||
                multiplicativeAdversList.find((m) => m.startsWith(parts[0].trim().toLowerCase()));

            let validEligibleNounFound = temporalNouns;
            if (parts.length > 1) {
                const temp = Array.from(parts);
                temp.shift();
                const remainingText = temp.join(' ').trim() || text;

                validEligibleNounFound = temporalNouns.filter((t) =>
                    t.toLowerCase().includes(remainingText.toLowerCase()),
                );
            }

            if (temporalNounFound) {
                allOptions.push(
                    ...validEligibleNounFound.map(
                        (tn) =>
                            `${
                                typeof temporalNounFound === 'boolean'
                                    ? parts[0]
                                    : temporalNounFound
                            } ${tn}`,
                    ),
                );
            } else {
                allOptions.push(
                    ...getAllMultiplicativeAdverbsCombinations().filter((t) => t.includes(text)),
                );
            }
        } else {
            //  none is present, we have to add all
            allOptions.push(...getAllMultiplicativeAdverbsCombinations());
        }
    }

    const isTemporalNounPatten = text.match(temporalNounRegexWithNumber);
    if (isTemporalNounPatten && isTemporalNounPatten.length && text.indexOf('-') === -1) {
        const no = isTemporalNounPatten[0].split(' ')[0];
        const parsedInt = parseInt(no);
        if (!isNaN(parsedInt) && parsedInt > 0) {
            const op = temporalNouns.map(
                (tn) => `${no} ${getUnit('time', no).toLowerCase()} ${tn}`,
            );
            allOptions.push(...op);
        } else {
            const op = temporalNouns
                .map((tn) =>
                    (nos.length ? nos : [4, 5, 6]).map(
                        (n) => `${n} ${getUnit('time', n.toString()).toLowerCase()} ${tn}`,
                    ),
                )
                .flat()
                .filter((item) => item.includes(text));
            allOptions.push(...op);
        }
    }

    const isEveryXHourlyPattern = text.match(everyXHourly);
    if (
        isEveryXHourlyPattern &&
        isEveryXHourlyPattern.length &&
        isEveryXHourlyPattern[0].length &&
        text.indexOf('-') === -1
    ) {
        if (parts.length === 3) {
            const no = parts[1];
            allOptions.push(`every ${no} hourly`);
        } else {
            allOptions.push(
                ...(nos.length ? nos : [1, 2, 3, 4])
                    .map((t) => `every ${t} hourly`)
                    .filter((t) => t.toLowerCase().includes(text.toLowerCase())),
            );
        }
    }

    const ifOnceEveryPattern = text.match(onceEvery);
    if (
        ifOnceEveryPattern &&
        ifOnceEveryPattern.length &&
        ifOnceEveryPattern[0].length &&
        text.indexOf('-') === -1
    ) {
        const nonDigits = text
            .replace(/[0-9]/g, '')
            .split(' ')
            .map((t) => t.trim())
            .filter(Boolean);
        if (parts.length >= 3) {
            const no = parts[2];
            if (parts[2] && parts[3]?.trim()) {
                // const unit = parts[3] && getUnit(parts[3], no);
                allOptions.push(
                    ...pureTemporalNouns
                        .filter((item) => item.startsWith(parts[3]))
                        .map(
                            (t) => `${parts[0]} every ${no} ${getUnit(t, no).toLowerCase() || ''}`,
                        ),
                );
            } else if (parts[2]) {
                const n = !isNaN(parseInt(parts[2].trim()))
                    ? [parts[2].trim()]
                    : nos.length
                    ? nos
                    : undefined;
                allOptions.push(
                    ...generateOnceEveryOptions(
                        multiplicativeAdverbs.has(parts[0].trim()) ? [parts[0]] : undefined,
                        n,
                    ).filter((t) =>
                        !nonDigits.length
                            ? false
                            : nonDigits.map((p) => t.includes(p)).filter(Boolean).length > 0,
                    ),
                );
            } else {
                allOptions.push(
                    ...generateOnceEveryOptions(
                        multiplicativeAdverbs.has(parts[0].trim()) ? [parts[0]] : undefined,
                        nos,
                    ).filter((t) =>
                        !nonDigits.length
                            ? false
                            : nonDigits.map((p) => t.includes(p)).filter(Boolean).length > 0,
                    ),
                );
            }
        } else {
            allOptions.push(
                ...generateOnceEveryOptions(
                    multiplicativeAdverbs.has(parts[0].trim()) ? [parts[0]] : undefined,
                    nos,
                ).filter((t) => {
                    if (parts.length === 1 && !isNaN(parseInt(parts[0].trim()))) {
                        return true;
                    }

                    return !nonDigits.length
                        ? false
                        : nonDigits.map((p) => t.includes(p)).filter(Boolean).length > 0;
                }),
            );
        }
    }

    const isStaticAbbr = staticFuse.search(text);
    if (isStaticAbbr && isStaticAbbr.length) {
        allOptions.push(...isStaticAbbr.map((t) => t.item));
    }

    if (!supportedDosesSet.has(parseInt(text).toString())) {
        allOptions.push(...appendCodedOptions(text));
    }

    return new Promise((resolve, _) => {
        // sort all options by startsWith text
        allOptions.sort((a, b) => {
            if (b.replace('every', '').trim().toLowerCase().startsWith(text.toLowerCase())) {
                return 1;
            }
            if (a.replace('every', '').trim().toLowerCase().startsWith(text.toLowerCase())) {
                return -1;
            }
            return 0;
        });

        // const dynamicFuse = new Fuse(allOptions, fuseConfig);
        // const options = dynamicFuse.search(text).map((t) => t.item);
        if (!allOptions.find((opitem) => opitem.toLowerCase().startsWith(text.toLowerCase()))) {
            allOptions.push(text);
        }
        resolve(allOptions);
    });
};

const addOneDose = (str: string, options: string[] = supportedDoses): string[] => {
    const parts = new Set(str.split('-'));
    const newOp = [...options].sort((a, b) => {
        if (a === '0') {
            return -1;
        }

        if (b === '0') {
            return 1;
        }

        if (parts.has(a)) {
            return -1;
        }

        if (parts.has(b)) {
            return 1;
        }

        return 0;
    });

    return newOp.map((d) => `${str}-${d}`);
};

const getAllMultiplicativeAdverbsCombinations = (): string[] => {
    return temporalNouns.map((tn) => multiplicativeAdversList.map((ma) => `${ma} ${tn}`)).flat();
};

const appendCodedOptions = (text: string = ''): string[] => {
    const op: string[] = [];
    const cleanText = text.replaceAll(' ', '').replace(/[^0-9-\/]/g, '');
    const isCodedHypenPattern = cleanText.match(codedHypenPattern);
    if (isCodedHypenPattern && isCodedHypenPattern.length && isCodedHypenPattern[0]) {
        const matchResult = isCodedHypenPattern[0];
        // write a loop to generate all possible combinations with supportedDoses and currentParts equals to 3 or 4 and push to allOptions
        // for example: 0-0-1/2, 0-1/2-0, 0-1/2-1/2, 1/2-1/2-1/2, 1/2-0-0, 1/2-0-1/2, 1/2-1/2-0

        let str = matchResult;
        let last: string | undefined = undefined;
        const parts = str.split('-');
        let t: string[] = [str];
        if (str.trim().endsWith('/')) {
            if (parts.length > 1) {
                last = parts.pop();
                str = parts.join('-');
                t = [str];
            } else {
                last = parts[0];
                t = supportedDoses.filter((t) => t.startsWith(last || ''));
                str = `${t[0]}`;
            }
        }
        let options: string[] = t
            .map((str) =>
                str.split('-').length < minDoseInTheCodePatten
                    ? addOneDose(
                          str,
                          last !== undefined
                              ? supportedDoses.filter((t) => t.startsWith(last || ''))
                              : undefined,
                      )
                    : [str],
            )
            .flat();

        while (str?.split('-').length < minDoseInTheCodePatten - 1) {
            options = options.map((o) => addOneDose(o)).flat();
            str = options[0];
        }

        op.push(...options);

        let temp = maxDoseInTheCodePatten - minDoseInTheCodePatten;
        while (temp > 0) {
            options = options.map((o) => addOneDose(o)).flat();
            temp--;
            op.push(...options);
        }
    }

    const filteredList = op.filter((t) => balckListedPatterns.has(t) === false);

    filteredList.sort((a, b) => {
        const aParts = a.split('-');
        const bParts = b.split('-');

        if (aParts.length !== bParts.length) {
            return aParts.length - bParts.length;
        }

        const aPartsSet = new Set(aParts);
        const bPartsSet = new Set(bParts);

        if (aPartsSet.size !== bPartsSet.size) {
            return aPartsSet.size - bPartsSet.size;
        }

        // const aScore = aParts
        //     .map((t, index) => supportedDoses.indexOf(t) * (supportedDoses.length - index) + 1)
        //     .reduce((a, b) => a + b, 0);
        // const bScore = bParts
        //     .map((t, index) => supportedDoses.indexOf(t) * (supportedDoses.length - index) + 1)
        //     .reduce((a, b) => a + b, 0);

        // if (aScore !== bScore) {
        //     return aScore - bScore;
        // }

        if (aParts[0] === '0' && bParts[0] === '0') {
            const fractionsInA = aParts.filter((t) => t.includes('/'));
            const fractionsInB = bParts.filter((t) => t.includes('/'));

            if (fractionsInA.length !== fractionsInB.length) {
                return fractionsInA.length - fractionsInB.length;
            }

            for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
                if (supportedDoses.indexOf(aParts[i]) > supportedDoses.indexOf(bParts[i])) {
                    return 1;
                } else if (supportedDoses.indexOf(aParts[i]) < supportedDoses.indexOf(bParts[i])) {
                    return -1;
                }
            }
        } else {
            for (let i = 0; i < Math.min(aParts.length, bParts.length); i++) {
                if (supportedDoses.indexOf(aParts[i]) > supportedDoses.indexOf(bParts[i])) {
                    return 1;
                } else if (supportedDoses.indexOf(aParts[i]) < supportedDoses.indexOf(bParts[i])) {
                    return -1;
                }
            }

            const fractionsInA = aParts.filter((t) => t.includes('/'));
            const fractionsInB = bParts.filter((t) => t.includes('/'));

            if (fractionsInA.length !== fractionsInB.length) {
                return fractionsInA.length - fractionsInB.length;
            }
        }

        return 0;
        // return aParts.length - bParts.length;
    });

    return filteredList;
};

function generateOnceEveryOptions(multiplicativeList?: string[], no?: string[]): string[] {
    return (no?.length ? no : [1, 2, 3, 4])
        .map((t) =>
            pureTemporalNouns
                .map((y) =>
                    (multiplicativeList?.length
                        ? multiplicativeList
                        : multiplicativeAdversList
                    ).map((m) => `${m} every ${t} ${getUnit(y, t.toString()).toLowerCase()}`),
                )
                .flat(),
        )
        .flat();
}
